import { ConfirmModal } from '@src/components/common/ConfirmModal';
import {
  clampMenuToViewport,
  ContextMenuItem,
  ContextMenuSurface,
} from '@src/components/common/contextMenuStyles';
import { useOutsideAlerter } from '@src/components/transcript/useClickOutside';
import { setCaptionsHidden } from '@src/services/karaoke/captionsVisibility';
import DeleteIcon from '@src/svgs/DeleteIcon';
import { KaraokeCard } from '@src/types.ts/general';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { useKaraokeStore, useVideoCreatorStore } from '../../../stores-v2';
import CirclePlusIcon from '../../../svgs/CirclePlusIcon';
import PresetPreview from '../PresetPreview';

import type { Preset, Template } from '../../textProcessor/TextBrandingService';
import type { KaraokeConfig } from '@src/services/karaoke/karaokeTypes';
import type { KaraokeTextBranding } from '@src/types.ts/story';
import type { MouseEvent } from 'react';

const logger = LogManager.getLogger('components:KaraokeTemplateSelect');

const MENU_WIDTH = 196;
const MENU_HEIGHT = 44;

// `Template` is keyed by the COMMON branding keys, so the karaoke-only
// `isGlobalPreset` needs a narrowing read.
const isLinkedGlobalPreset = (template: Template): boolean =>
  Boolean((template as Partial<KaraokeTextBranding>).isGlobalPreset);

// Mostly copied from GlobalKaraokePresetSelect.tsx, but adapted for user templates
export default function KaraokeTemplateSelect({
  config,
  produceKaraokeText,
  templates,
  onAddTemplateButtonClick,
}: {
  config: KaraokeConfig;
  produceKaraokeText: () => Promise<void>;
  templates: Template[];
  onAddTemplateButtonClick: () => void;
}) {
  const videoCreator = useVideoCreatorStore();
  const karaokeStore = useKaraokeStore();
  const karaokeLine =
    videoCreator.rootStore.editorStore?.editor?.getKaraokeLines();
  const hasKaraoke =
    (karaokeStore?.hasKaraokeLines && !!karaokeLine?.[0]?.id) ?? false;
  // PLAT-11877 — captions turned Off from the player's Captions picker keep
  // their track (drawn invisible), but no template is ON the video then, so
  // no card is selected — the picker and the cards say the same thing.
  const captionsOn = hasKaraoke && !karaokeStore?.captionsHidden;
  // PLAT-11877 — while nothing is on the video, the card a first "captions
  // on" applies (the one rule, `getDefaultCaptionTemplateConfig`) says so.
  const defaultTemplateId = captionsOn
    ? null
    : (videoCreator.textBrandingService.getDefaultCaptionTemplateConfig(
        videoCreator.getCurrentAspectRatio(),
      )?.templateId ?? null);

  const settingsDisabled = !hasKaraoke || !config.parent_template_id;

  // PLAT-11416 — right-click a workspace template card for its actions;
  // Remove Template permanently deletes the record (confirmed first).
  const [menu, setMenu] = useState<{
    x: number;
    y: number;
    template: Template;
  } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<Template | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  useOutsideAlerter(menuRef, () => setMenu(null));

  const onSelectPreset = async (template: Preset) => {
    await produceKaraokeText();
    await videoCreator.textBrandingService.applyTemplate(
      template,
      'karaoke',
      null,
      undefined,
      false,
    );
    videoCreator.setActiveElements();
    // PLAT-11877 — a card pick while the captions are Off (hidden, from the
    // player's Captions picker) is a request to see them: the template goes
    // on, so the captions come back. Cleared after the produce and apply,
    // which both keep whatever visibility the config carried.
    const editor = videoCreator.rootStore.editorStore?.editor;
    if (
      editor &&
      setCaptionsHidden(editor, false, { createUndoRedoEntry: false })
    ) {
      karaokeStore.syncFromEditor(editor);
      videoCreator.rootStore.videoSourceStore?.refreshSource();
    }
    // Persist the applied caption template so it survives navigating
    // back to Content Studio. Without this the selection is only in
    // memory and the next reload reverts to the previous track.
    await videoCreator.saveStoryAndVideo();
  };

  const handleSettingsClick = () => {
    if (!settingsDisabled) {
      videoCreator.karaokeSubMenu = KaraokeCard.styles;
    }
  };

  const openTemplateMenu = (event: MouseEvent, template: Template) => {
    // Global Arbor preset RECORDS can be linked into the workspace list —
    // never offer to remove those (deleteKaraokeTemplate refuses them too).
    if (isLinkedGlobalPreset(template)) return;
    event.preventDefault();
    const { x, y } = clampMenuToViewport(
      event.clientX,
      event.clientY,
      MENU_WIDTH,
      MENU_HEIGHT,
    );
    setMenu({ x, y, template });
  };

  const removeTemplate = async (template: Template) => {
    setConfirmRemove(null);
    const templateId = template.id?.toString();
    if (!templateId) return;
    try {
      await videoCreator.textBrandingService.deleteKaraokeTemplate(templateId);
    } catch (error) {
      logger.error('Failed to remove caption template', {
        templateId,
        ...extractErrorInfo(error),
      });
      videoCreator.showToast({
        state: 'error',
        message: 'Failed to remove the caption template',
      });
    }
  };

  return (
    <Section>
      <AddTemplateRow onClick={onAddTemplateButtonClick}>
        <ClickableIconWrapper type="button" aria-label="Add new template">
          <CirclePlusIcon width="20" height="20" color={'currentColor'} />
        </ClickableIconWrapper>
        <AddTemplateLabel>Add Custom Template</AddTemplateLabel>
      </AddTemplateRow>
      <PresetsContainer>
        {templates.map((t, i) => (
          <TemplateCardWrapper
            key={`${t?.id}-${t?.aspectRatio}-${i}`}
            onContextMenu={event => openTemplateMenu(event, t)}
          >
            <PresetPreview
              preset={t as Preset}
              onSelectPreset={onSelectPreset}
              selected={
                captionsOn &&
                t?.id?.toString() === config.parent_template_id?.toString()
              }
              badge={
                defaultTemplateId !== null &&
                t?.id?.toString() === defaultTemplateId
                  ? 'Default'
                  : undefined
              }
              onSettingsClick={
                captionsOn &&
                t?.id?.toString() === config.parent_template_id?.toString()
                  ? handleSettingsClick
                  : undefined
              }
            />
          </TemplateCardWrapper>
        ))}
      </PresetsContainer>
      {menu &&
        createPortal(
          <ContextMenuSurface
            ref={menuRef}
            style={{ top: menu.y, left: menu.x }}
          >
            <ContextMenuItem
              $danger
              onClick={() => {
                setConfirmRemove(menu.template);
                setMenu(null);
              }}
            >
              <DeleteIcon strokeColor="currentColor" /> Remove Template
            </ContextMenuItem>
          </ContextMenuSurface>,
          document.body,
        )}
      {confirmRemove && (
        <ConfirmModal
          title="Remove Caption Template"
          message={`"${confirmRemove.title}" will be removed for everyone in this workspace. This can't be undone.`}
          confirmText="Remove"
          cancelText="Cancel"
          closeModal={() => setConfirmRemove(null)}
          onCancel={() => setConfirmRemove(null)}
          onConfirm={() => removeTemplate(confirmRemove)}
        />
      )}
    </Section>
  );
}

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex-shrink: 0;
  align-self: stretch;
`;

const AddTemplateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  transition: color var(--transition-normal) ease;
  color: var(--text-muted);
  &:hover {
    color: var(--primary);
  }
`;

const AddTemplateLabel = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: inherit;
`;

const PresetsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-self: stretch;
`;

/* Context-menu hit area for a card; no visual role of its own (min-width: 0
 * keeps the grid track from growing past its column). */
const TemplateCardWrapper = styled.div`
  min-width: 0;
`;

export const ClickableIconWrapper = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 4px;
  margin: -4px; /* expand hit area without shifting layout */
  line-height: 0;
`;
